import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { rpc, useRpcEvent } from '../api/rpc'
import type { Connector, ConnectorTool } from '../api/types'
import { useWorkspaces } from '../components/useWorkspaces'
import { RepoAdmin } from '../components/repos/RepoAdmin'
import { Button } from '../components/ui/Button'
import { Badge, EmptyState } from '../components/ui/Card'
import {
  Cell, Count, FilterChips, Pager, Row, RowMenu, SearchBox, Table, Td, Th, Toolbar, useTable, type Rail
} from '../components/ui/DataTable'
import { Checkbox, Field, Segmented } from '../components/ui/Field'
import { Select } from '../components/ui/Select'
import { Modal } from '../components/ui/Modal'
import { confirmDialog } from '../components/ui/Confirm'
import { useToast } from '../components/ui/Toast'
import { Dropdown, type MenuItem } from '../components/ui/Popover'
import { IconChevronDown, IconPlus } from '../components/icons'
import { ConnectorCatalog, type CatalogItemId, type CatalogStatus } from '../components/connectors/Catalog'
import { FigmaTokenForm, GithubConnectForm, JiraConnectForm, SocialTokenForm, ZimbraLoginForm } from '../components/connectors/forms'
import { McpServerForm } from '../components/connectors/McpForm'
import { formatDateTime } from '../lib/format'

/**
 * Connectors (docs/web-ui.md §6): one ledger table lists every connector; clicking a row opens a
 * detail popup. Adding, policies, sharing and reconnecting all happen inside popups, never inline.
 */

/* ------------------------------------------------------------------ data --- */

export function useConnectors() {
  const [items, setItems] = useState<Connector[] | null>(null)
  const load = useCallback(() => rpc.call<{ items: Connector[] }>('connectors.list').then(r => setItems(r.items)).catch(() => undefined), [])
  useEffect(() => { void load() }, [load])
  useRpcEvent('connector.updated', () => void load())
  useRpcEvent('connector.removed', () => void load())
  return { items, reload: load }
}

/* --------------------------------------------------------------- helpers --- */

/** Brand names are kept literal, like the rest of the app. */
const KIND_LABEL: Record<Connector['kind'], string> = { google: 'Google', telegram: 'Telegram', mcp: 'MCP', social: 'Meow Social', figma: 'Figma', zimbra: 'Zimbra Mail', jira: 'Jira', github: 'GitHub' }
const PRODUCTS = ['sheets', 'drive', 'docs', 'calendar', 'gmail'] as const

const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
const asStringList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

/** Row rail: green connected, red error, grey not authorised yet. */
const railOf = (c: Connector): Rail => (c.status === 'connected' ? 'ok' : c.status === 'error' ? 'error' : 'idle')
const policyRail = (p: ConnectorTool['policy']): Rail => (p === 'deny' ? 'error' : p === 'ask' ? 'attention' : 'ok')

/** Quiet second line of the connector cell: account, endpoint or command. */
function connectorAccount(c: Connector): string {
  const cfg = c.config
  if (c.kind === 'mcp') {
    if (cfg.transport === 'stdio') return `⌘ ${asString(cfg.command) ?? ''} ${asStringList(cfg.args).join(' ')}`.trim()
    return asString(cfg.url) ?? ''
  }
  if (c.kind === 'telegram') { const u = asString(cfg.username); return u ? `@${u}` : '' }
  if (c.kind === 'jira') {
    const name = asString(cfg.displayName) || asString(cfg.username)
    const host = asString(cfg.host)
    return name && host ? `${name} · ${host}` : name || host || ''
  }
  return asString(cfg.email) ?? ''
}

function StatusBadge({ c }: { c: Connector }) {
  const { t } = useTranslation()
  return <Badge tone={c.status === 'connected' ? 'success' : c.status === 'error' ? 'danger' : 'neutral'}>{t(`connectors.status_${c.status}`)}</Badge>
}

/** Section heading used inside the detail popup. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 font-mono text-[11px] uppercase tracking-[0.08em] text-ink-500">{children}</h3>
}

/* ---------------------------------------------------------- tool policies --- */

/** Tools of one connector with a per-tool permission picker (the old ToolPolicies, as a table). */
function ToolPoliciesTable({ connector }: { connector: Connector }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [tools, setTools] = useState<ConnectorTool[] | null>(null)
  const load = useCallback(
    () => rpc.call<{ items: ConnectorTool[] }>('connectors.tools', { connectorId: connector.id })
      .then(r => setTools(r.items)).catch(() => setTools([])),
    [connector.id]
  )
  useEffect(() => { void load() }, [load])

  if (tools === null) return null
  if (tools.length === 0) return <EmptyState title={t('connectors.toolsEmpty')} body={t('connectors.toolsEmptyBody')} />
  return (
    <Table minWidth={520}>
      <thead>
        <tr>
          <Th rail />
          <Th>{t('connectors.colTools')}</Th>
          <Th>{t('teams.description')}</Th>
          <Th>{t('connectors.policy')}</Th>
        </tr>
      </thead>
      <tbody>
        {tools.map(tool => (
          <Row key={tool.name} rail={policyRail(tool.policy)}>
            <Td><span className="font-mono text-xs font-semibold">{tool.name}</span></Td>
            <Td className="text-xs text-ink-500">{tool.description}</Td>
            <Td>
              <div className="w-36">
                <Select
                  size="sm" label={t('connectors.policy')}
                  value={tool.policy}
                  onChange={async v => {
                    try {
                      await rpc.call('connectors.setToolPolicy', { connectorId: connector.id, tool: tool.name, policy: v === tool.defaultPolicy ? null : v })
                      void load()
                    } catch (err) { toast((err as Error).message, 'error') }
                  }}
                  options={(['allow', 'ask', 'deny'] as const).map(p => ({
                    value: p,
                    label: `${t(`connectors.policy_${p}`)}${p === tool.defaultPolicy ? ` (${t('connectors.default')})` : ''}`
                  }))}
                />
              </div>
            </Td>
          </Row>
        ))}
      </tbody>
    </Table>
  )
}

/* ------------------------------------------------------------ team share --- */

/** Owner-only: share a connector with teams (docs/teams.md). Member counts are not exposed. */
function TeamShare({ connectorId }: { connectorId: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const { items: workspaces } = useWorkspaces()
  const teams = workspaces.filter(w => w.kind === 'team' && w.role !== 'viewer')
  const [shared, setShared] = useState<string[]>([])
  useEffect(() => {
    if (teams.length) void rpc.call<{ teamIds: string[] }>('connectors.shares', { connectorId }).then(r => setShared(r.teamIds)).catch(() => undefined)
  }, [connectorId, teams.length])
  if (!teams.length) return null
  return (
    <section>
      <SectionLabel>{t('connectors.detailShare')}</SectionLabel>
      <div className="space-y-2 text-sm">
        {teams.map(w => (
          <Checkbox
            key={w.teamId} label={w.name} checked={shared.includes(w.teamId!)}
            onChange={async e => {
              if (e.target.checked && !(await confirmDialog(t('teams.shareWarning'), { danger: false, confirmLabel: t('teams.share') }))) return
              try { setShared((await rpc.call<{ teamIds: string[] }>('connectors.share', { connectorId, teamId: w.teamId, shared: e.target.checked })).teamIds) }
              catch (err) { toast((err as Error).message, 'error') }
            }}
          />
        ))}
        <p className="text-xs text-ink-500">{t('teams.shareWarning')}</p>
      </div>
    </section>
  )
}

/* --------------------------------------------------------- kind sections --- */

/** Telegram link step + notify/approval settings (moved out of the old page card). */
function TelegramSettings({ connector, reload }: { connector: Connector; reload(): void }) {
  const { t } = useTranslation()
  const toast = useToast()
  const cfg = connector.config
  const ownChatId = asString(cfg.ownChatId) ?? (typeof cfg.ownChatId === 'number' ? String(cfg.ownChatId) : undefined)
  const username = asString(cfg.username)
  const startCode = asString(cfg.startCode)
  const ownName = asString(cfg.ownName)
  const approvals = asString(cfg.approvals) ?? 'routines'
  const update = async (patch: Record<string, unknown>) => {
    try { await rpc.call('connectors.telegram.update', { connectorId: connector.id, ...patch }); reload() }
    catch (err) { toast((err as Error).message, 'error') }
  }
  return (
    <section className="space-y-3 text-sm">
      {ownChatId ? (
        <p className="text-ink-400">{t('connectors.telegram.linked', { name: ownName ?? ownChatId })}</p>
      ) : (
        <div className="rounded-lg border border-ginger-500/40 bg-ginger-500/10 p-3">
          <p>{t('connectors.telegram.linkStep')}</p>
          <a className="mt-2 inline-block font-semibold text-ginger-600 underline" target="_blank" rel="noopener noreferrer" href={`https://t.me/${username}?start=${startCode}`}>
            {t('connectors.telegram.openBot', { bot: connector.name })}
          </a>
          <p className="mt-1 text-xs text-ink-500">{t('connectors.telegram.manual', { code: startCode })}</p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Checkbox label={t('connectors.telegram.notify')} checked={cfg.notify === true} onChange={e => void update({ notify: e.target.checked })} />
        <Field label={t('connectors.telegram.approvals')}>{id => (
          <Select
            id={id} value={approvals} onChange={v => void update({ approvals: v })}
            options={[
              { value: 'routines', label: t('connectors.telegram.approvals_routines') },
              { value: 'all', label: t('connectors.telegram.approvals_all') },
              { value: 'off', label: t('connectors.telegram.approvals_off') },
            ]}
          />
        )}</Field>
      </div>
      <p className="text-xs text-ink-500">{t('connectors.telegram.groupHint')}</p>
    </section>
  )
}

/* ---------------------------------------------------------- detail modal --- */

function ConnectorDetail({ connector, onClose, reload, onEdit }: { connector: Connector; onClose(): void; reload(): void; onEdit(): void }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [tokenForm, setTokenForm] = useState(false)
  const failing = connector.status === 'error'
  const canReconnect = (connector.kind === 'jira' || connector.kind === 'zimbra' || connector.kind === 'figma' || connector.kind === 'social' || connector.kind === 'github') || (failing && (connector.kind === 'google' || connector.kind === 'mcp'))
  const granted = asStringList(connector.config.products)
  const missing = PRODUCTS.filter(p => !granted.includes(p))

  const disconnect = async () => {
    if (!await confirmDialog(t('connectors.confirmRemove', { name: connector.name }))) return
    try { await rpc.call('connectors.remove', { connectorId: connector.id }); onClose() }
    catch (err) { toast((err as Error).message, 'error') }
  }
  const check = () => toast(t(`connectors.status_${connector.status}`), connector.status === 'connected' ? 'success' : 'error')
  const goGoogle = async (products: string[]) => {
    try { window.location.assign((await rpc.call<{ url: string }>('connectors.google.authUrl', { products })).url) }
    catch (err) { toast((err as Error).message, 'error') }
  }
  const reconnect = async () => {
    if (connector.kind === 'google') return void goGoogle(granted.length ? granted : [...PRODUCTS])
    if (connector.kind === 'mcp') {
      try { window.location.assign((await rpc.call<{ authUrl: string }>('connectors.mcp.reauthorize', { connectorId: connector.id })).authUrl) }
      catch (err) { toast((err as Error).message, 'error') }
      return
    }
    setTokenForm(true)
  }
  const refreshMcp = async () => {
    try { await rpc.call('connectors.mcp.refresh', { connectorId: connector.id }); toast(t('connectors.mcp.refreshed'), 'success'); reload() }
    catch (err) { toast((err as Error).message, 'error') }
  }

  return (
    <Modal
      open wide title={connector.name} description={t('connectors.detailHint')} onClose={onClose}
      footer={
        <>
          <Button variant="danger" className="mr-auto" onClick={() => void disconnect()}>{t('connectors.remove')}</Button>
          <Button variant="secondary" onClick={() => (canReconnect ? void reconnect() : check())}>
            {canReconnect ? t('connectors.reconnect') : t('connectors.checkConnection')}
          </Button>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        </>
      }
    >
      <div className="space-y-5">
        {connector.lastError && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-700">{connector.lastError}</p>}

        <dl className="grid grid-cols-1 gap-x-5 gap-y-3 text-sm sm:grid-cols-[190px_1fr]">
          <dt className="text-ink-500">{t('nav.account')}</dt>
          <dd className="font-mono text-[13px]">{connectorAccount(connector)}</dd>
          <dt className="text-ink-500">{t('admin.users.status')}</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <StatusBadge c={connector} />
            <span className="text-xs text-ink-500">{t('connectors.colLastOk')} · {connector.lastOkAt ? formatDateTime(connector.lastOkAt, i18n.language) : t('connectors.lastOkNever')}</span>
          </dd>
          {connector.kind === 'mcp' && asStringList(connector.config.headerKeys).length > 0 && (
            <>
              <dt className="text-ink-500">{t('connectors.mcp.headers')}</dt>
              <dd className="flex flex-wrap gap-1.5">{asStringList(connector.config.headerKeys).map(h => <Badge key={h}><span className="font-mono">{h}</span></Badge>)}</dd>
            </>
          )}
          {connector.kind === 'google' && (
            <>
              <dt className="text-ink-500">{t('connectors.google.enabled')}</dt>
              <dd className="flex flex-wrap gap-1.5">
                {granted.length
                  ? granted.map(p => <Badge key={p}>{t(`connectors.google.product_${p}`)}</Badge>)
                  : <span className="text-ink-500">{t('common.none')}</span>}
              </dd>
            </>
          )}
        </dl>

        {/* Kind-specific actions moved out of the old page cards. */}
        {connector.kind === 'google' && missing.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {missing.map(p => (
              <Button key={p} size="sm" variant="outline" onClick={() => void goGoogle([...granted, p])}>{`+ ${t(`connectors.google.product_${p}`)}`}</Button>
            ))}
          </div>
        )}
        {connector.kind === 'telegram' && <TelegramSettings connector={connector} reload={reload} />}
        {connector.kind === 'social' && (connector.status !== 'connected' || tokenForm) && (
          <SocialTokenForm reconnect onDone={() => { setTokenForm(false); reload() }} />
        )}
        {connector.kind === 'figma' && (connector.status !== 'connected' || tokenForm) && (
          <FigmaTokenForm reconnect onDone={() => { setTokenForm(false); reload() }} />
        )}
        {connector.kind === 'zimbra' && (connector.status !== 'connected' || tokenForm) && (
          <ZimbraLoginForm reconnect email={typeof connector.config.email === 'string' ? connector.config.email : ''} onDone={() => { setTokenForm(false); reload() }} />
        )}
        {connector.kind === 'jira' && (connector.status !== 'connected' || tokenForm) && (
          <JiraConnectForm reconnect host={typeof connector.config.host === 'string' ? connector.config.host : ''} onDone={() => { setTokenForm(false); reload() }} />
        )}
        {connector.kind === 'github' && (connector.status !== 'connected' || tokenForm) && (
          <GithubConnectForm reconnect onDone={() => { setTokenForm(false); reload() }} appSlug={typeof connector.config.appSlug === 'string' ? connector.config.appSlug : null} />
        )}
        {connector.kind === 'github' && connector.status === 'connected' && (
          <RepoAdmin connectorId={connector.id} onChanged={reload} />
        )}
        {connector.kind === 'mcp' && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={onEdit}>{t('connectors.mcp.edit')}</Button>
            <Button size="sm" variant="ghost" onClick={() => void refreshMcp()}>{t('connectors.mcp.refresh')}</Button>
          </div>
        )}

        <section>
          <SectionLabel>{t('connectors.detailTools')}</SectionLabel>
          <ToolPoliciesTable connector={connector} />
        </section>

        {/* A GitHub connector carries the user's own credential, so it is never shared. */}
        {connector.kind !== 'github' && <TeamShare connectorId={connector.id} />}
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------------ page --- */

export function ConnectorsPage({ extra }: { extra?: (items: Connector[]) => ReactNode }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { items, reload } = useConnectors()
  const [params, setParams] = useSearchParams()
  const [detailId, setDetailId] = useState<string | null>(null)
  const [toolCounts, setToolCounts] = useState<Record<string, number>>({})
  const [editId, setEditId] = useState<string | null>(null)
  const [catalogQuery, setCatalogQuery] = useState('')
  const [addRequest, setAddRequest] = useState<CatalogItemId | null>(null)
  const clearAddRequest = useCallback(() => setAddRequest(null), [])
  const [status, setStatus] = useState<CatalogStatus>({ google: true, social: false, zimbra: false, sandbox: false, githubApp: null })
  const [socialUrl, setSocialUrl] = useState<string | null>(null)
  useEffect(() => {
    void rpc.call<{ configured: boolean }>('connectors.google.status').then(g => setStatus(s => ({ ...s, google: g.configured }))).catch(() => undefined)
    void rpc.call<{ configured: boolean; url: string | null }>('connectors.social.status')
      .then(x => { setStatus(s => ({ ...s, social: x.configured })); setSocialUrl(x.url) }).catch(() => undefined)
    void rpc.call<{ configured: boolean }>('connectors.zimbra.status').then(x => setStatus(s => ({ ...s, zimbra: x.configured }))).catch(() => undefined)
    void rpc.call<{ enabled: boolean }>('sandbox.status').then(x => setStatus(s => ({ ...s, sandbox: x.enabled }))).catch(() => undefined)
    void rpc.call<{ configured: boolean; appSlug: string | null }>('connectors.github.status').then(x => setStatus(s => ({ ...s, githubApp: x.configured ? x.appSlug : null }))).catch(() => undefined)
  }, [])
  // Tabs like Claude's connector settings: what you have, and a directory of what you can add.
  const tabParam = params.get('tab')
  const tab: 'yours' | 'discover' = tabParam === 'discover' || tabParam === 'yours' ? tabParam : items && items.length === 0 ? 'discover' : 'yours'
  const setTab = (v: 'yours' | 'discover') => setParams(p => { const n = new URLSearchParams(p); n.set('tab', v); return n }, { replace: true })
  const add = (id: CatalogItemId) => { setTab('discover'); setAddRequest(id) }

  // Query-parameter toast after a provider redirect (?google=… / ?mcp=…).
  useEffect(() => {
    const g = params.get('google')
    const m = params.get('mcp')
    const h = params.get('github')
    if (!g && !m && !h) return
    if (g === 'partial') {
      const list = (params.get('missing') ?? '').split(',').filter(Boolean).map(p => t(`connectors.google.product_${p}`)).join(', ')
      toast(t('connectors.google.partial', { list }), 'error')
    } else if (g === 'connected' || m === 'connected') toast(t(g ? 'connectors.google.connected' : 'connectors.mcp.added'), 'success')
    else if (h === 'connected') toast(t('connectors.github.connected'), 'success')
    else if (h) toast(`${t('connectors.github.failed')}: ${params.get('message') ?? ''}`, 'error')
    else toast(`${t(g ? 'connectors.google.failed' : 'connectors.mcp.failed')}: ${params.get('message') ?? ''}`, 'error')
    setParams({ tab: 'yours' }, { replace: true })
  }, [params, setParams, toast, t])

  // The list RPC has no tool count, so one `connectors.tools` call per connector fills the column.
  // Keyed by the connector id list (a string), so a reload with the same connectors does not refetch
  // and a failed call resolves to 0 instead of retrying.
  const idKey = (items ?? []).map(c => c.id).join(',')
  useEffect(() => {
    const ids = idKey ? idKey.split(',') : []
    if (ids.length === 0) { setToolCounts({}); return }
    let alive = true
    void Promise.all(ids.map(async id => {
      try { return [id, (await rpc.call<{ items: ConnectorTool[] }>('connectors.tools', { connectorId: id })).items.length] as const }
      catch { return [id, 0] as const }
    })).then(pairs => { if (alive) setToolCounts(Object.fromEntries(pairs)) })
    return () => { alive = false }
  }, [idKey])

  const table = useTable(items, {
    search: c => `${c.name} ${c.kind} ${connectorAccount(c)}`,
    filter: (c, f) => (f === 'error' ? c.status === 'error' : f === 'pending' ? c.status === 'disconnected' : true)
  })

  if (!items) return null

  const connected = items.filter(c => c.status === 'connected').length
  const errors = items.filter(c => c.status === 'error').length
  const pending = items.filter(c => c.status === 'disconnected').length
  const toolsTotal = Object.values(toolCounts).reduce((a, b) => a + b, 0)
  const detail = items.find(c => c.id === detailId) ?? null
  const editing = items.find(c => c.id === editId && c.kind === 'mcp') ?? null

  const disconnect = async (c: Connector) => {
    if (!await confirmDialog(t('connectors.confirmRemove', { name: c.name }))) return
    try { await rpc.call('connectors.remove', { connectorId: c.id }); setDetailId(null) }
    catch (err) { toast((err as Error).message, 'error') }
  }
  const menu = (c: Connector): MenuItem[] => [
    { label: t('common.open'), onSelect: () => setDetailId(c.id) },
    ...(c.kind === 'mcp' ? [{ label: t('connectors.mcp.edit'), onSelect: () => setEditId(c.id) }] : []),
    { label: t('connectors.checkConnection'), onSelect: () => toast(t(`connectors.status_${c.status}`), c.status === 'connected' ? 'success' : 'error') },
    { label: t('connectors.remove'), danger: true, divider: true, onSelect: () => void disconnect(c) }
  ]

  const sep = <span aria-hidden="true" className="text-ink-500">·</span>
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-line pb-4">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-500">{t('settings.group.workspace')} / {t('connectors.title')}</div>
          <h1 className="mt-2.5 font-display text-[30px] font-bold leading-tight tracking-tight">{t('connectors.title')}</h1>
          <p className="mt-2.5 max-w-[70ch] text-sm text-ink-400">{t('connectors.subtitle')}</p>
          <p className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-ink-400">
            <span><span className="font-semibold text-ink-200">{connected}</span> {t('connectors.status_connected')}</span>
            {errors > 0 && <>{sep}<span className="font-semibold text-red-700">{errors} {t('connectors.status_error')}</span></>}
            {pending > 0 && <>{sep}<span className="font-semibold text-amber-700">{pending} {t('connectors.pending')}</span></>}
            {sep}
            <span><span className="font-semibold text-ink-200">{toolsTotal}</span> {t('connectors.colTools')}</span>
          </p>
        </div>
      </header>

      {/* Toolbar like Claude's connector settings: tabs on the left, search and Add on the right. */}
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center justify-between gap-2.5 sm:contents">
          <Segmented<'yours' | 'discover'>
            value={tab} onChange={setTab} label={t('connectors.title')}
            options={[
              { value: 'yours', label: <>{t('connectors.catalog.tabYours')}{items.length > 0 && <span className="ml-1.5 font-mono text-[11px] text-ink-500">{items.length}</span>}</> },
              { value: 'discover', label: t('connectors.catalog.tabDiscover') }
            ]}
          />
          <div className="sm:order-3 sm:shrink-0">
            <Dropdown
              label={t('connectors.add')}
              width={260}
              items={[
                { label: t('connectors.catalog.mcpRemote'), onSelect: () => add('mcp-remote') },
                { label: t('connectors.catalog.mcpLocal'), disabled: !status.sandbox, onSelect: () => add('mcp-local') },
                { label: 'Jira', onSelect: () => add('jira') },
                { label: t('connectors.catalog.browse'), divider: true, onSelect: () => setTab('discover') }
              ]}
              trigger={p => (
                <Button {...p} ref={p.ref}><IconPlus width={16} height={16} />{t('connectors.catalog.addButton')}<IconChevronDown width={14} height={14} /></Button>
              )}
            />
          </div>
        </div>
        {tab === 'discover' && (
          <SearchBox
            className="w-full sm:order-2 sm:ml-auto sm:w-64 sm:flex-none"
            value={catalogQuery}
            onChange={setCatalogQuery}
            placeholder={t('connectors.catalog.search')}
          />
        )}
      </div>

      {tab === 'discover' ? (
        <ConnectorCatalog
          items={items} status={status} socialUrl={socialUrl} query={catalogQuery}
          onOpen={id => { setTab('yours'); setDetailId(id) }}
          onAdded={() => { reload(); setTab('yours') }}
          request={addRequest} onRequestHandled={clearAddRequest}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('connectors.empty')} body={t('connectors.emptyBody')}
          action={<Button variant="secondary" onClick={() => setTab('discover')}>{t('connectors.catalog.browse')}</Button>}
        />
      ) : (
        <>
          <Toolbar className="flex-col items-stretch gap-2.5 sm:flex-row sm:items-center">
            <SearchBox
              className="w-full sm:w-64 sm:flex-none"
              value={table.query}
              onChange={table.setQuery}
              placeholder={t('connectors.searchPlaceholder')}
            />
            <div className="flex w-full items-center justify-between gap-2 sm:contents">
              <FilterChips
                value={table.filter} onChange={table.setFilter} label={t('connectors.filterLabel')}
                options={[
                  { value: 'all', label: t('common.all') },
                  { value: 'error', label: t('connectors.filter_error') },
                  { value: 'pending', label: t('connectors.filter_pending') }
                ]}
              />
              <Count>{t('connectors.count', { count: table.total })}</Count>
            </div>
          </Toolbar>

          <Table
            minWidth={720}
            footer={
              <>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="inline-flex items-center gap-1.5"><i className="inline-block h-3.5 w-[3px] rounded-sm bg-mint-500" aria-hidden="true" />{t('connectors.status_connected')}</span>
                  <span className="inline-flex items-center gap-1.5"><i className="inline-block h-3.5 w-[3px] rounded-sm bg-red-700" aria-hidden="true" />{t('connectors.status_error')}</span>
                  <span className="inline-flex items-center gap-1.5"><i className="inline-block h-3.5 w-[3px] rounded-sm bg-cream-400" aria-hidden="true" />{t('connectors.pending')}</span>
                </div>
                <div className="ml-auto"><Pager page={table.page} pages={table.pages} onChange={table.setPage} /></div>
              </>
            }
          >
            <thead>
              <tr>
                <Th rail />
                <Th>{t('connectors.title')}</Th>
                <Th>{t('connectors.mcp.kind')}</Th>
                <Th>{t('admin.users.status')}</Th>
                <Th><span className="block text-right">{t('connectors.colTools')}</span></Th>
                <Th>{t('connectors.colLastOk')}</Th>
                <Th><span className="sr-only">{t('common.actions')}</span></Th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map(c => (
                <Row key={c.id} rail={railOf(c)} onClick={() => setDetailId(c.id)}>
                  <Td><Cell title={c.name} sub={connectorAccount(c)} /></Td>
                  <Td><Badge>{KIND_LABEL[c.kind]}</Badge></Td>
                  <Td><StatusBadge c={c} /></Td>
                  <Td className="text-right font-mono text-[13px]">{toolCounts[c.id] ? toolCounts[c.id] : t('connectors.noToolsCount')}</Td>
                  <Td className="text-xs text-ink-500">{c.lastOkAt ? formatDateTime(c.lastOkAt, i18n.language) : t('connectors.lastOkNever')}</Td>
                  <Td><RowMenu items={menu(c)} /></Td>
                </Row>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {extra?.(items)}

      {detail && (
        <ConnectorDetail
          connector={detail} onClose={() => setDetailId(null)} reload={reload}
          onEdit={() => { setDetailId(null); setEditId(detail.id) }}
        />
      )}
      {editing && (
        <Modal open wide title={t('connectors.mcp.editTitle', { name: editing.name })} onClose={() => setEditId(null)}>
          <McpServerForm connector={editing} onDone={() => { setEditId(null); reload() }} onCancel={() => setEditId(null)} />
        </Modal>
      )}
    </div>
  )
}
